import { isInTransit, getInTransitLabel } from 'wsm-highlighted-attribute';

const VALID_ODOMETER_VALUE = /^(?!0+(?:\s|,|$))\d{1,3}(?:,\d{3})*/;

/**
 * Copy postfixed preference values to their un-postfixed keys in a preference object.
 * E.g. copy the value of 'titleHeadingColor-sidebar' to 'titleHeadingColor' and remove
 * 	'titleHeadingColor-sidebar' from the object.
 * @param {object} prefsObj - An object of { preference: value, ... }
 * @param {string} modifier - An identifier postfixed to differentiate preferences (e.g. '-sidebar')
 */
export const remapPrefs = (prefsObj, modifier) => {
	const newPrefs = { ...prefsObj };

	// Extract preferences postfixed with modifier
	Object.keys(newPrefs)
		.filter((pref) => pref.includes(modifier) && newPrefs[pref] !== '')
		.forEach((pref) => {
			newPrefs[pref.replace(modifier, '')] = prefsObj[pref];
		});

	// Remove postfixed preferences and copy new pref values over the original ones
	const filteredPrefs = Object.keys(newPrefs)
		.filter((key) => !key.includes(modifier))
		.reduce(
			(obj, key) => ({
				...obj,
				[key]: newPrefs[key]
			}),
			{}
		);

	return filteredPrefs;
};

/**
 * Create formatted search request object
 * @param {string} vehicleId
 * @returns {object} - Formatted search request object
 */
export const getSearchRO = (vehicleId) => {
	return {
		vehicle: {
			rql: `&select(type,certified,normalFuelType,estimatedDeliveryDate,status,classification,make,stockNumber)&eq(uuid,${vehicleId})`
		}
	};
};

/**
 * replaces custom tags [city] and [state] with values from account address
 * @param {string} subTitleTemplate - preference value with SEO tags
 * @param {object} accountAddress - from ADD query
 * @returns string
 */
export const replaceSeoTags = (subTitleTemplate, accountAddress) => {
	const city = accountAddress.city || '';
	const state = accountAddress.state || '';

	let subTitle = subTitleTemplate.replace(/\[city\]/gi, city); // Replace city
	subTitle = subTitle.replace(/\[state\]/gi, state); // Replace the state

	return subTitle;
};

export const getAccountId = (rData) => {
	return (
		(rData.parameterMap && rData.parameterMap.accountId) || rData.accountId
	);
};

/**
 * Format the elements of the array vehicleAttributes with a separator specified from prefs
 * @param {object} vehicleTitleAttrs - array of attribute objects returned from item display config service
 * @param {string} vehicleTitleSeparator - pref value to be concatenated as separator of vehicle attributes, Ex: '|'
 * @returns array of strings with separator added except for the last item. ex: [ "Item1 |", "Item2 |", "item3 |", "item4" ]
 */
export const formatVehicleAttrsWithSeparator = (
	vehicleTitleAttrs,
	vehicleTitleSeparator
) =>
	vehicleTitleAttrs.map((a, index) => {
		const attrValue = typeof a === 'string' ? a.trim() : a;
		return index < vehicleTitleAttrs.length - 1
			? `${attrValue} <span aria-hidden="true">${vehicleTitleSeparator}</span>`
			: attrValue;
	});

/**
 * take array of title attributes from Item Display Config, and returns array of strings split
 * @param {object} attrs - array of attribute objects returned from item display config service
 * @param {object} labels - i18n labels loaded by widget from labels.json
 * @param {string} type - vehicle type attribute (new/used)
 * @param {boolean} isCertified - vehicle certified attribute
 * @param {string} forceShowVehicleCondition - pref value
 * @param {string} showVehicleTitleSeparators - pref value
 * @param {string} vehicleTitleSeparator - pref value
 * @returns array of strings
 */
export const getVehicleTitle = (
	attrs,
	labels,
	type,
	isCertified,
	forceShowVehicleCondition,
	showVehicleTitleSeparators,
	vehicleTitleSeparator
) => {
	const NEXT_LINE = '\u0085';

	const vehicleTitleAttrs = attrs
		.filter(({ name }) => name !== 'lineBreak') // Filter out lineBreak attributes
		.reduce((result, { name, itemValue }) => {
			result.push(itemValue);
			if (name === 'make') {
				result.push(NEXT_LINE);
			}
			return result;
		}, []);

	// TODO: Remove this forceVehicleType override when we have a better solution
	if (
		forceShowVehicleCondition === 'true' &&
		!attrs.some((e) => e.name === 'type')
	) {
		const vehicleType = isCertified
			? 'CERTIFIED_PRE_OWNED'
			: type.toUpperCase();
		vehicleTitleAttrs.unshift(labels.get(vehicleType));
	}

	if (showVehicleTitleSeparators === 'true') {
		// TODO: This solution can be removed when forcelineBreak is also removed
		// The next line arbitrarily searches for the first linebreak ocurrence because
		// the component always uses the position 0 of the array for vehicle attributes
		const indexOfFirstNextLine = vehicleTitleAttrs.indexOf(NEXT_LINE);
		const attributesWithSeparator = formatVehicleAttrsWithSeparator(
			vehicleTitleAttrs.slice(0, indexOfFirstNextLine),
			vehicleTitleSeparator
		);
		// Then rebuild the array with the original order but with position 0 already formatted
		return [
			...attributesWithSeparator,
			...vehicleTitleAttrs.slice(
				indexOfFirstNextLine,
				vehicleTitleAttrs.length
			)
		]
			.join(' ')
			.split(NEXT_LINE)
			.map((s) => s.trim());
	}
	return vehicleTitleAttrs
		.join(' ')
		.split(NEXT_LINE)
		.map((s) => s.trim());
};

export const formatDeliveryDate = (estimatedDeliveryDate, locale) => {
	return new Date(estimatedDeliveryDate).toLocaleDateString(locale, {
		month: 'short',
		day: '2-digit'
	});
};

export const isExistAttrConfig = (attrs, attrName) => {
	return !!attrs.find((attr) => attr.name === attrName);
};

export const isVehicleMakeMatchAccountFranchise = (attrs, make) => {
	const certified = attrs.find((config) => config.name === 'certified');
	if (certified && Array.isArray(certified.computeAttrs)) {
		return certified.computeAttrs.includes(make);
	}
	return false;
};

/**
 * Checks if a vehicle account is a descendant of an automotive group account.
 *
 * @param {string} accountId - Account ID to check
 * @param {string[]} descendantAccountIds - Array of descendant account IDs
 * @param {string} verticalType - Account vertical type
 * @returns {boolean} True if vehicle belongs to the group account
 */
export const isDescendant = (accountId, descendantAccountIds, verticalType) => {
	const isGroupAccount =
		verticalType === 'AUTOMOTIVE_DEALERSHIP_GROUP' ||
		verticalType === 'AUTOMOTIVE_PORTAL';
	if (!isGroupAccount) {
		return false; // Not a group account, so vehicle is not a descendant
	}
	// Handle null or undefined descendantAccountIds
	if (!Array.isArray(descendantAccountIds)) {
		return false;
	}
	return descendantAccountIds.includes(accountId);
};

const getStatusLabel = (
	status,
	labels,
	estimatedDeliveryDate,
	locale,
	isOffSiteVehicle
) => {
	if (isInTransit(status)) {
		return getInTransitLabel({
			estimatedDeliveryDate,
			locale,
			labels
		});
	}
	if (isOffSiteVehicle) {
		return labels.get('OFF_SITE_HIGHLIGHTED_ATTRIBUTE');
	}
	return labels.get('ON_SITE_HIGHLIGHTED_ATTRIBUTE');
};

/**
 * Take array of supplemental title attributes from Item Display Config, and returns supplemental title string
 * @param {object} supplementalTitle - Array of attribute objects returned from item display config service
 * @param {object} labels - i18n labels loaded by widget from labels.json
 * @param {string} estimatedDeliveryDate - Estimated delivery date for in-transit vehicles
 * @param {string} locale - Locale string for date formatting (e.g., 'en-US')
 * @param {boolean} isOffSiteVehicle - Whether the vehicle is off-site
 * @returns {string} Formatted supplemental title string with attributes separated by '|'
 */
export const getSupplementalTitle = (
	supplementalTitle,
	labels,
	estimatedDeliveryDate,
	locale,
	isOffSiteVehicle
) => {
	return supplementalTitle
		.reduce((acc, { name, itemValue }) => {
			let label = itemValue;
			switch (name) {
				case 'odometer': {
					if (!VALID_ODOMETER_VALUE.test(itemValue)) {
						return acc;
					}
					break;
				}
				case 'status':
					label = getStatusLabel(
						itemValue,
						labels,
						estimatedDeliveryDate,
						locale,
						isOffSiteVehicle
					);
					break;
				default:
					break;
			}

			return label ? `${acc} ${acc ? '|' : ''} ${label}` : acc;
		}, '')
		.trim();
};

export const isNotBlankAndNotEmpty = (value) => {
	return value !== 'BLANK' && String(value).trim() !== '';
};

/**
 * Build metadata attribute line with formatted spans and separators
 * @param {Array} items - Array of { label, value } objects
 * @param {Object} options - Configuration options
 * @param {string} options.attributeClass - CSS class for attribute spans (default: 'sub-title-attribute')
 * @param {string} options.separatorClass - CSS class for separator spans (default: 'sub-title-separator')
 * @returns {string} HTML string with formatted attributes and separators
 */
export const buildMetadataAttributeLine = (items, options = {}) => {
	const {
		attributeClass = 'sub-title-attribute',
		separatorClass = 'sub-title-separator'
	} = options;

	const validItems = items.filter((item) => item.value);

	if (validItems.length === 0) return '';

	return validItems
		.map(
			(item) =>
				`<span class="${attributeClass}">${item.label}: ${item.value}</span>`
		)
		.join(`<span class="${separatorClass}" aria-hidden="true"> | </span>`);
};
